// Player Chronic's shared pure computation — extracted out of PlayerChronic.js (2026-09-10) so
// PlayerChronicPanel.js (the Einstein compose panel) reuses the exact same weekly-grouping logic
// instead of a second hand-copy. Same extraction pattern as gps/matchChronicShared.js.
import { getGPS, getPlayers } from '../../api';
import { dateToSeason } from '../../SeasonContext';

export function getISOWeek(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

export function getISOYear(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  return d.getFullYear();
}

// Sortable composite key for year+week, e.g. "2026-20"
export function isoKey(dateStr) {
  return `${getISOYear(dateStr)}-${String(getISOWeek(dateStr)).padStart(2, '0')}`;
}

// One player's sessions (TD/RD/HID/SD/SA/Expl/HMLD), season-filtered, ascending by date — the
// same per-session shape PlayerChronic.js's own `playerSessions` memo builds.
export function computePlayerSessions(playerId, sessions, selectedSeasons = []) {
  return (sessions || [])
    .flatMap(s =>
      (s.players || [])
        .filter(p => String(p.player_id) === String(playerId))
        .map(p => ({
          date: (s.session_date || '').slice(0, 10),
          title: s.session_title || s.title || '',
          td: Number(p.td) || 0, rd: Number(p.rd) || 0, hid: Number(p.hid) || 0,
          sd: Number(p.sd) || 0, sa: Number(p.sa) || 0, expl: Number(p.expl) || 0, hmld: Number(p.hmld) || 0,
        }))
    )
    .filter(s => {
      if (!s.date) return false;
      const season = dateToSeason(s.date);
      return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Sessions grouped into ISO weeks, most recent week first — same shape as PlayerChronic.js's
// own `allWeeks` memo.
export function groupIntoWeeks(playerSessions) {
  const map = new Map();
  for (const s of playerSessions) {
    const key = isoKey(s.date);
    if (!map.has(key)) map.set(key, { key, weekNum: getISOWeek(s.date), year: getISOYear(s.date), sessions: [] });
    map.get(key).sessions.push(s);
  }
  return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));
}

const sumField = (arr, key) => arr.reduce((s, x) => s + (Number(x[key]) || 0), 0);

// A week bucket's TD/RD/HID/SA/HMLD totals — used by the compact panel's weekly rows.
export function weekTotals(week) {
  return {
    td: sumField(week.sessions, 'td'), rd: sumField(week.sessions, 'rd'),
    hid: sumField(week.sessions, 'hid'), sa: sumField(week.sessions, 'sa'),
    hmld: sumField(week.sessions, 'hmld'), sessionCount: week.sessions.length,
  };
}

// Shared reference-data fetch for PlayerChronicPanel.js — team-wide sessions/players, one fetch
// feeding N per-player panels, same pattern as fetchMatchChronicPanelData.
export async function fetchPlayerChronicPanelData() {
  const [sessions, players] = await Promise.all([getGPS(), getPlayers()]);
  return { sessions: sessions || [], players: players || [] };
}
